"use client";

import { useWeb3 } from "logic/components";
import { BatchCall, useERC20Allowance } from "logic/hooks";
import { fundWallet } from "logic/hooks/privy/fundWallet";
import { ERC20Metadata, OUSDaiUtilityAddresses } from "logic/utils";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { erc20Abi_bytes32, zeroAddress } from "viem";
import { useBalance } from "wagmi";

import { OAdapterAbi, OUSDaiUtilityAbi } from "../contracts";
import { useLoanDetails } from "../LoanDetails/LoanDetailsProvider";
import { useDstChainSP } from "../useRepayTx/useBuyTx/useDstChainSP";
import { useSrcChainSP } from "../useRepayTx/useBuyTx/useSrcChainSP";

export enum DepositType {
  Deposit,
}

interface useBatchBuyTxParams {
  depositAmountUnits: bigint;
  depositToken: ERC20Metadata;
  enabled?: boolean;
}

export function useBatchBuyTx(params: useBatchBuyTxParams) {
  const { chainId: mainChainId, reserveAccount: recipient } = useLoanDetails();

  const { depositAmountUnits, depositToken, enabled = true } = params;
  const depositType = DepositType.Deposit;

  const isBridging = depositToken.chainId !== mainChainId;

  const { address: account = zeroAddress } = useWeb3();

  const { dstChainSP, lzReceiveGasLimit } = useDstChainSP({ recipient });
  const { srcChainSP, depositParamsData, srcChainSendQuote, srcChainSendQuoteError } = useSrcChainSP({
    depositAmountUnits,
    depositToken,
    depositMainToken: depositToken.mainToken,
    depositType,
    dstChainSP,
    lzReceiveGasLimit,
    isBridging,
    enabled,
  });

  const isTxEnabled = Boolean(enabled && depositAmountUnits && account !== zeroAddress);

  /**************************************************************************/
  /* Erc20 Approval */
  /**************************************************************************/
  const spender = isBridging ? depositToken.OftAdapter : OUSDaiUtilityAddresses[mainChainId];

  const { data: allowance, error: allowanceError } = useERC20Allowance({
    erc20Address: depositToken.address,
    spenderAddress: spender,
    chainId: depositToken.chainId,
    enabled: Boolean(isTxEnabled),
    watch: true,
  });
  const isApproved = allowance != undefined ? allowance >= depositAmountUnits : undefined;

  /**************************************************************************/
  /* Native balance check + fund wallet (cross-chain only) */
  /**************************************************************************/
  const txValue = srcChainSendQuote?.nativeFee;

  const { data: nativeBalance } = useBalance({
    chainId: depositToken.chainId,
    address: account,
    query: {
      enabled: Boolean(enabled && isBridging && account !== zeroAddress),
    },
  });

  useEffect(() => {
    if (
      isBridging &&
      account !== zeroAddress &&
      txValue !== undefined &&
      nativeBalance !== undefined &&
      nativeBalance.value < txValue
    ) {
      const promise = fundWallet({
        address: account,
        amount: txValue * 3n,
        chainId: depositToken.chainId,
      });
      toast.promise(promise, {
        loading: "Funding wallet...",
        success: "Wallet funded",
        error: "Failed to fund wallet",
      });
    }
  }, [account, txValue, nativeBalance, depositToken.chainId, isBridging]);

  /**************************************************************************/
  /* Batched Calls */
  /**************************************************************************/
  const { calls, isReady } = useMemo(() => {
    const _calls: BatchCall[] = [];

    if (isBridging && !srcChainSendQuote) return { calls: [], isReady: false };
    if (!isApproved) {
      _calls.push({
        address: depositToken.address,
        abi: erc20Abi_bytes32,
        functionName: "approve",
        args: [spender, depositAmountUnits],
        chainId: depositToken.chainId,
      });
    }
    if (isBridging) {
      _calls.push({
        value: srcChainSendQuote?.nativeFee,
        abi: OAdapterAbi,
        address: depositToken.OftAdapter,
        functionName: "send",
        args: [{ ...srcChainSP }, srcChainSendQuote, account],
        chainId: depositToken.chainId,
      });
    } else {
      _calls.push({
        abi: OUSDaiUtilityAbi,
        address: OUSDaiUtilityAddresses[mainChainId],
        functionName: "localCompose",
        value: 0n,
        args: [depositType, depositToken.address, depositAmountUnits, depositParamsData],
        chainId: mainChainId,
      });
    }
    return { calls: _calls, isReady: true };
  }, [
    account,
    depositAmountUnits,
    depositParamsData,
    depositToken,
    isApproved,
    isBridging,
    mainChainId,
    spender,
    srcChainSP,
    srcChainSendQuote,
    depositType,
  ]);

  return {
    calls,
    isEnabled: isTxEnabled && isReady,
    error: allowanceError ?? srcChainSendQuoteError,
  };
}
